"use client";

import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";

export interface KalshiLiveCompetitor {
  id?: string;
  roundScores?: number[];
  currentRoundScore?: string;
  overallScore?: number;
  seed?: number;
  serving?: boolean;
}

export interface KalshiLiveSnapshot {
  type: string;
  status?: string;
  widgetStatus?: string;
  competitors?: KalshiLiveCompetitor[];
  homeScore?: number;
  awayScore?: number;
  homeTeamId?: string;
  awayTeamId?: string;
  period?: number;
  periodLabel?: string;
  clock?: string;
  winner?: string;
  balls?: number;
  strikes?: number;
  outs?: number;
  inningHalf?: 'top' | 'bot';
}

async function fetchLiveState(): Promise<Record<string, KalshiLiveSnapshot>> {
  const res = await fetch("/api/markets/kalshi/live-state");
  if (!res.ok) return {};
  const data = (await res.json()) as { state?: Record<string, KalshiLiveSnapshot> };
  const out: Record<string, KalshiLiveSnapshot> = {};
  for (const [k, v] of Object.entries(data.state || {})) out[k.toUpperCase()] = v;
  return out;
}

function getWsUrl(): string | null {
  if (typeof window === 'undefined') return null;
  // In dev the Next server (3000) doesn't proxy raw WS upgrades to the backend
  // (4000), so we need an explicit URL. Prod uses same-origin behind a proxy.
  const explicit = process.env.NEXT_PUBLIC_BACKEND_WS_URL;
  if (explicit) return explicit.replace(/\/$/, '') + '/wss/widget';
  const fromHttp = process.env.NEXT_PUBLIC_BACKEND_URL;
  if (fromHttp) {
    return fromHttp.replace(/^http/, 'ws').replace(/\/$/, '') + '/wss/widget';
  }
  if (window.location.host.startsWith('localhost')) {
    return `ws://localhost:4000/wss/widget`;
  }
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${window.location.host}/wss/widget`;
}

/**
 * Live state map for Kalshi sports events.
 *
 * Strategy:
 *   1. Bootstrap with one HTTP fetch of the full snapshot map (cached by React
 *      Query, deduped across consumers).
 *   2. Open one shared widget WS and subscribe to `source: 'kalshi-live'` for
 *      all tickers we know about. Apply diffs to local state.
 *   3. Fallback to 10s polling if the WS never opens.
 *
 * Returns the same shape consumers already use: `state[ticker.toUpperCase()]`.
 */
export function useKalshiLiveState(enabled = true) {
  const [overlay, setOverlay] = useState<Record<string, KalshiLiveSnapshot>>({});
  const wsRef = useRef<WebSocket | null>(null);
  const wsConnectedRef = useRef(false);
  const subscribedRef = useRef<Set<string>>(new Set());

  // Bootstrap fetch — also serves as fallback when WS is unavailable.
  const query = useQuery<Record<string, KalshiLiveSnapshot>>({
    queryKey: ["kalshi", "live-state"],
    queryFn: fetchLiveState,
    enabled,
    refetchInterval: () => (wsConnectedRef.current ? false : 10_000),
    staleTime: 8_000,
    gcTime: 5 * 60 * 1000,
  });

  // Open the shared widget WS once, subscribe to whatever tickers we have.
  useEffect(() => {
    if (!enabled) return;
    const url = getWsUrl();
    if (!url) return;

    let ws: WebSocket | null = null;
    let closed = false;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let backoff = 1000;

    const connect = () => {
      try {
        ws = new WebSocket(url);
        wsRef.current = ws;
      } catch {
        return;
      }

      ws.onopen = () => {
        wsConnectedRef.current = true;
        backoff = 1000;
        // Subscribe to whatever tickers we already know about from bootstrap
        const tickers = Array.from(subscribedRef.current);
        if (tickers.length > 0) {
          ws?.send(JSON.stringify({ action: 'subscribe', source: 'kalshi-live', tickers }));
        }
      };

      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          if (msg?.type === 'kalshi-live' && msg.data && typeof msg.data === 'object') {
            setOverlay((prev) => ({ ...prev, ...msg.data }));
          }
        } catch {}
      };

      ws.onclose = () => {
        wsConnectedRef.current = false;
        if (closed) return;
        if (reconnectTimer) clearTimeout(reconnectTimer);
        reconnectTimer = setTimeout(connect, backoff);
        backoff = Math.min(backoff * 2, 30_000);
      };

      ws.onerror = () => {
        try { ws?.close(); } catch {}
      };
    };

    connect();

    return () => {
      closed = true;
      wsConnectedRef.current = false;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      const sock = ws;
      if (sock) {
        if (sock.readyState === WebSocket.CONNECTING) {
          // Defer close until the handshake completes — closing a CONNECTING
          // socket triggers the noisy "closed before established" warning.
          sock.addEventListener('open', () => { try { sock.close(); } catch {} }, { once: true });
        } else if (sock.readyState === WebSocket.OPEN) {
          try { sock.close(); } catch {}
        }
      }
      wsRef.current = null;
    };
  }, [enabled]);

  // Whenever the bootstrap data refreshes, subscribe to any new tickers.
  useEffect(() => {
    if (!query.data) return;
    const newTickers: string[] = [];
    for (const t of Object.keys(query.data)) {
      const upper = t.toUpperCase();
      if (!subscribedRef.current.has(upper)) {
        subscribedRef.current.add(upper);
        newTickers.push(upper);
      }
    }
    if (newTickers.length > 0 && wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ action: 'subscribe', source: 'kalshi-live', tickers: newTickers }));
    }
  }, [query.data]);

  // Merge bootstrap + WS overlay so consumers always see the freshest snapshot.
  const data = query.data ? { ...query.data, ...overlay } : overlay;
  return { ...query, data };
}
